/**
 * The pure core of the schema-driven account form (spec §6).
 *
 * The platform's account ui-config schema is CLOSED: `component` is one of `textField` | `selectField`,
 * `key` is the literal `'name'` or the tuple `['options', '<fieldKey>']`, and the payload it wants is
 * FLAT — secrets live in `options` alongside everything else and config-backend splits them by the
 * definition's `secretFields`. Everything here follows from those two facts.
 */
import { fieldFormatError, portError } from '@server/account-field-format';
import type { AccountFormField } from '@server/rpc-types';
import { z } from 'zod';

export { fieldFormatError };

export type FieldKey = string;

export function comparableAccountName(name: string): string {
  return name.trim();
}

/** `'name'` → `'name'`; `['options', 'restApiKey']` → `'restApiKey'`. */
export function fieldKeyOf(field: AccountFormField): FieldKey {
  return Array.isArray(field.key) ? (field.key[field.key.length - 1] ?? '') : field.key;
}

function selectValues(field: AccountFormField): string[] {
  return (field.options ?? []).map((o: { value: string }) => o.value);
}

// No explicit `z.ZodType<Record<string, string>>` return annotation: that widening loses the exact
// `ZodObject` shape zodResolver's overloads key off (same fix as SnowflakeForm.tsx's `satisfies`
// pattern) and breaks `zodResolver(schema)` at the AccountForm call site. Callers still only see a
// `z.ZodType<Record<string, string>>`-compatible value; TS infers the precise object type here.
export function buildAccountFormSchema(
  fields: AccountFormField[],
  opts: {
    definitionName?: string;
    optionalKeys?: string[];
    secretKeys?: string[];
    /** Account names already in use. Compared exactly after trimming, like config-backend. */
    takenNames?: ReadonlySet<string>;
    takenNameMessage?: string;
  } = {},
) {
  const optional = new Set(opts.optionalKeys ?? []);
  const secret = new Set(opts.secretKeys ?? []);
  // Both type params matter: `z.ZodType<string>` alone leaves Input defaulting to `unknown`, which
  // makes the assembled object schema's input type `Record<string, unknown>` and breaks zodResolver's
  // generic match against `useForm<Record<string, string>>` at the AccountForm call site.
  const shape: Record<string, z.ZodType<string, string>> = {};
  for (const field of fields) {
    const key = fieldKeyOf(field);
    const isOptional = field.optional === true || optional.has(key);
    // Upstream ui-config can omit the integer marker on a port field.
    if (field.integer === true || key === 'port') {
      shape[key] = z.string().superRefine((value, ctx) => {
        if (value.trim() === '' && isOptional) return;
        const message =
          key === 'port' ? portError(value) : /^\d+$/u.test(value.trim()) ? null : 'Enter a whole number.';
        if (message !== null) ctx.addIssue({ code: 'custom', message });
      });
      continue;
    }
    let base: z.ZodType<string, string>;
    if (field.component === 'selectField') {
      const values = selectValues(field);
      // A select with no options is unusable; fall back to a free string rather than crash.
      base =
        values.length > 0 ? z.string().refine((v) => values.includes(v), `${field.label} is required`) : z.string();
      if (isOptional) base = base.or(z.literal(''));
    } else {
      base = z.string();
    }
    if (hasFormatRule(field)) {
      // Check secrets as typed and other values trimmed, as splitPlatformValues sends them.
      shape[key] = base.superRefine((v, ctx) => {
        if (v.trim() === '') {
          // A select with options already reports this through its membership check.
          if (!isOptional && (field.component !== 'selectField' || selectValues(field).length === 0)) {
            ctx.addIssue({ code: 'custom', message: `${field.label} is required` });
          }
          return;
        }
        const message = fieldFormatError(field.secret === true || secret.has(key) ? v : v.trim(), field, field.label);
        if (message !== null) ctx.addIssue({ code: 'custom', message });
      });
      continue;
    }
    if (field.component === 'selectField') {
      shape[key] = base;
      continue;
    }
    const text = isOptional ? z.string() : z.string().trim().min(1, `${field.label} is required`);
    const { takenNames, takenNameMessage } = opts;
    shape[key] =
      key === 'name' && takenNames && takenNameMessage
        ? text.refine(
            (v) => ![...takenNames].some((name) => comparableAccountName(name) === comparableAccountName(v)),
            takenNameMessage,
          )
        : text;
  }
  return z.object(shape);
}

const hasFormatRule = (field: AccountFormField) => field.pattern !== undefined || field.maxLength !== undefined;

export function defaultValuesFor(
  fields: AccountFormField[],
  account?: { name: string; options: Record<string, unknown> },
): Record<string, string> {
  const values: Record<string, string> = {};
  for (const field of fields) {
    const key = fieldKeyOf(field);
    if (key === 'name') {
      values[key] = account?.name ?? '';
      continue;
    }
    // A secret is never sent back to the browser, so it always starts blank — in edit mode blank
    // means "keep the stored value" (spec §D4). Only stringify primitives with a sane String()
    // form — an object/array option would otherwise render as the literal text "[object Object]".
    const stored = account?.options[key];
    if (typeof stored === 'string') {
      values[key] = stored;
      continue;
    }
    if (typeof stored === 'number' || typeof stored === 'boolean') {
      values[key] = String(stored);
      continue;
    }
    // Falls through here for a non-primitive stored option (object, array, or null) — guarding
    // against rendering the literal text "[object Object]" in the field. Under the audited closed
    // schema (accountFormSchema.ts module doc) every stored option is a string/number/boolean, so
    // this branch is unreachable today, same as the unknown-component fallback in AccountForm.tsx.
    // Kept as a defensive hedge rather than dead code to delete: if the platform ever adds a
    // non-primitive option value, a blank field beats a literal "[object Object]" in the UI.
    values[key] = account ? '' : typeof field.default === 'string' ? field.default : '';
  }
  return values;
}

/**
 * Build the flat `{ name, options }` the platform wants from the RENDERED fields only.
 *
 * That "rendered only" is safe because of one rule enforced server-side, not because of anything
 * here: config-backend REPLACES a provided `options` blob wholesale while merging its `secretFields`
 * per key, so a key this loop cannot reach is preserved when it is a secret and erased when it is
 * not. `lookoutSupportFor` (`src/lib/rudderstack-accounts.ts`) therefore refuses to offer a form at
 * all for a definition whose `config.optionFields` are not all rendered, and `updateRsAccount`
 * refuses such an options PATCH behind it (Vamsi L6). `omitKeys` is the same decision seen from the
 * other side — see `AccountForm`'s comment on why it is `config.secretFields` and nothing else.
 */
export function assemblePayload(
  fields: AccountFormField[],
  values: Record<string, string>,
  opts: { omitKeys?: string[] } = {},
): { name: string; options: Record<string, unknown> } {
  const omit = new Set(opts.omitKeys ?? []);
  let name = '';
  const options: Record<string, unknown> = {};
  for (const field of fields) {
    const key = fieldKeyOf(field);
    const raw = (values[key] ?? '').trim();
    if (key === 'name') {
      name = raw;
      continue;
    }
    if (raw === '' && omit.has(key)) continue;
    options[key] = raw;
  }
  return { name, options };
}
